import React, { useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import { useGoogleAuth } from '@questlabs/auth-js';
import SafeIcon from '../../common/SafeIcon';
import './EventRsvpPass.css';

const { FiDownload, FiPrinter, FiShare2 } = FiIcons;

function formatEventDate(value) {
  if (!value) return 'Date to be announced';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function createPassFilename(title, suffix = 'rsvp-pass') {
  const slug = String(title ?? 'memorial-event')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return `${slug || 'memorial-event'}-${suffix}.png`;
}

async function loadImage(url) {
  const image = new Image();
  image.src = url;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = reject;
  });
  return image;
}

async function svgToPngBlob(svg, size = 528) {
  const markup = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await loadImage(svgUrl);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not prepare the pass image.');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);
    context.drawImage(image, 0, 0, size, size);

    return await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Could not export the QR image.'));
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

function drawWrappedText(context, value, x, y, maxWidth, lineHeight, maxLines = 2) {
  const words = String(value ?? '').split(/\s+/).filter(Boolean);
  let line = '';
  let lineCount = 0;

  words.forEach((word, index) => {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width > maxWidth && line) {
      context.fillText(line, x, y + lineCount * lineHeight);
      lineCount += 1;
      line = word;
    } else {
      line = candidate;
    }

    if (index === words.length - 1 && lineCount < maxLines) {
      context.fillText(line, x, y + lineCount * lineHeight);
    }
  });
}

async function createFullPassImage({ rsvp, memorialName, attendee, qrSvg }) {
  const qrBlob = await svgToPngBlob(qrSvg, 528);
  const qrUrl = URL.createObjectURL(qrBlob);

  try {
    const qrImage = await loadImage(qrUrl);
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 680;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not prepare the pass image.');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#10243b';
    context.fillRect(0, 0, canvas.width, 220);
    context.fillStyle = '#d8b36a';
    context.fillRect(0, 0, 12, canvas.height);

    context.fillStyle = '#d8b36a';
    context.font = 'bold 22px Arial, sans-serif';
    context.letterSpacing = '5px';
    context.fillText('MEMORIAL SERVICE', 72, 66);
    context.letterSpacing = '0px';

    context.fillStyle = '#ffffff';
    context.font = 'bold 42px Georgia, serif';
    drawWrappedText(context, rsvp?.event_title || 'Event RSVP', 72, 126, 900, 48, 2);

    context.fillStyle = '#d8dce2';
    context.font = '24px Arial, sans-serif';
    context.fillText(memorialName || 'Memorial gathering', 72, 190);

    context.fillStyle = '#ffffff';
    context.fillRect(72, 280, 320, 320);
    context.drawImage(qrImage, 82, 290, 300, 300);

    context.fillStyle = '#7a8491';
    context.font = 'bold 18px Arial, sans-serif';
    context.fillText('ATTENDEE', 460, 330);
    context.fillStyle = '#10243b';
    context.font = 'bold 32px Arial, sans-serif';
    drawWrappedText(context, attendee || 'Guest', 460, 374, 540, 38, 2);

    context.fillStyle = '#7a8491';
    context.font = 'bold 18px Arial, sans-serif';
    context.fillText('WHEN', 460, 466);
    context.fillStyle = '#10243b';
    context.font = '26px Arial, sans-serif';
    drawWrappedText(context, formatEventDate(rsvp?.event_date), 460, 508, 540, 34, 2);

    context.fillStyle = '#7a8491';
    context.font = 'bold 18px Arial, sans-serif';
    context.fillText('PASS REFERENCE', 460, 588);
    context.fillStyle = '#10243b';
    context.font = 'bold 22px monospace';
    context.fillText(
      rsvp?.id != null ? `RSVP-${rsvp.id}` : `EVENT-${rsvp?.event_id ?? 'PASS'}`,
      460,
      624,
    );

    return await new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Could not export the full pass image.'));
      }, 'image/png');
    });
  } finally {
    URL.revokeObjectURL(qrUrl);
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function EventRsvpPass({ rsvp, memorialName }) {
  const cardRef = useRef(null);
  const { user } = useGoogleAuth();
  const [feedback, setFeedback] = useState('');
  const attendee = user?.name || user?.email?.split('@')?.[0] || 'Guest';
  const qrValue = JSON.stringify({
    type: 'memorial-event-rsvp',
    rsvpId: String(rsvp?.id ?? ''),
    eventId: String(rsvp?.event_id ?? ''),
    eventTitle: rsvp?.event_title ?? '',
    eventDate: rsvp?.event_date ?? '',
  });

  const getQrImage = async () => {
    const svg = cardRef.current?.querySelector('svg');
    if (!svg) throw new Error('The QR pass is not available yet.');
    return svgToPngBlob(svg);
  };

  const saveQr = async () => {
    setFeedback('');
    try {
      downloadBlob(await getQrImage(), createPassFilename(rsvp?.event_title, 'rsvp-qr'));
      setFeedback('QR image saved. Keep it handy for event check-in.');
    } catch (error) {
      setFeedback(error?.message || 'Could not save this QR image.');
    }
  };

  const saveFullPass = async () => {
    setFeedback('');
    try {
      const svg = cardRef.current?.querySelector('svg');
      if (!svg) throw new Error('The QR pass is not available yet.');
      const blob = await createFullPassImage({ rsvp, memorialName, attendee, qrSvg: svg });
      downloadBlob(blob, createPassFilename(rsvp?.event_title));
      setFeedback('Full RSVP pass saved as a PNG image.');
    } catch (error) {
      setFeedback(error?.message || 'Could not save the full pass image.');
    }
  };

  const sharePass = async () => {
    setFeedback('');
    const title = rsvp?.event_title ?? 'Memorial event';
    const reference = rsvp?.id != null ? `RSVP-${rsvp.id}` : `EVENT-${rsvp?.event_id ?? 'PASS'}`;
    const text = `${title} RSVP pass · ${reference} · ${formatEventDate(rsvp?.event_date ?? '')}`;

    try {
      if (navigator.share) {
        if (navigator.canShare && navigator.canShare({ files: [] })) {
          const blob = await getQrImage();
          const file = new File([blob], createPassFilename(title, 'rsvp-qr'), { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ title, text, files: [file] });
            return;
          }
        }
        await navigator.share({ title, text });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        setFeedback('Pass details copied to your clipboard.');
        return;
      }
      setFeedback('Sharing is not available in this browser.');
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setFeedback(error?.message || 'Could not share this pass.');
      }
    }
  };

  const printPass = () => {
    const card = cardRef.current;
    if (!card) return;
    document.body.classList.add('printing-rsvp-pass');
    card.classList.add('print-this-rsvp-pass');

    const cleanup = () => {
      document.body.classList.remove('printing-rsvp-pass');
      card.classList.remove('print-this-rsvp-pass');
      window.removeEventListener('afterprint', cleanup);
    };

    window.addEventListener('afterprint', cleanup, { once: true });
    window.print();
  };

  return (
    <article
      ref={cardRef}
      className="rsvp-pass-print-target overflow-hidden rounded-2xl border border-brand-dark bg-white"
    >
      <div className="bg-accent-navy px-5 py-4 text-white">
        <p className="text-[9px] font-black uppercase tracking-[0.2em] text-accent-gold">Memorial service</p>
        <h5 className="mt-1 font-serif text-lg">{rsvp?.event_title ?? 'Event RSVP'}</h5>
        <p className="mt-1 text-xs text-white/70">{memorialName || 'Memorial gathering'}</p>
      </div>
      <div className="flex flex-col items-center gap-4 p-5 sm:flex-row sm:items-start">
        <div className="rounded-xl border border-brand-light bg-white p-2">
          <QRCodeSVG
            value={qrValue}
            size={132}
            level="M"
            includeMargin
            aria-label={`QR RSVP pass for ${rsvp?.event_title ?? 'memorial event'}`}
          />
        </div>
        <div className="min-w-0 flex-1 text-center sm:text-left">
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Attendee</p>
          <p className="mt-1 break-words font-semibold text-accent-navy">{attendee}</p>
          <p className="mt-4 text-[9px] font-black uppercase tracking-widest text-text-muted">When</p>
          <p className="mt-1 text-sm text-accent-navy">{formatEventDate(rsvp?.event_date ?? '')}</p>
          <p className="mt-4 text-[9px] font-black uppercase tracking-widest text-text-muted">Pass reference</p>
          <p className="mt-1 font-mono text-xs text-text-muted">
            {rsvp?.id != null ? `RSVP-${rsvp.id}` : `EVENT-${rsvp?.event_id ?? 'PASS'}`}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-3 border-t border-brand-light px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[10px] text-text-muted">Present this pass at the door.</p>
        <div className="rsvp-pass-actions flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={saveFullPass}
            className="rsvp-pass-action inline-flex items-center gap-2 rounded-lg bg-brand-light px-3 py-2 text-[9px] font-black uppercase tracking-widest text-accent-navy transition hover:bg-accent-gold"
          >
            <SafeIcon icon={FiDownload} className="h-3.5 w-3.5" />
            Save pass
          </button>
          <button
            type="button"
            onClick={saveQr}
            className="rsvp-pass-action inline-flex items-center gap-2 rounded-lg bg-brand-light px-3 py-2 text-[9px] font-black uppercase tracking-widest text-accent-navy transition hover:bg-accent-gold"
          >
            <SafeIcon icon={FiDownload} className="h-3.5 w-3.5" />
            Save QR
          </button>
          <button
            type="button"
            onClick={sharePass}
            className="rsvp-pass-action inline-flex items-center gap-2 rounded-lg bg-brand-light px-3 py-2 text-[9px] font-black uppercase tracking-widest text-accent-navy transition hover:bg-accent-gold"
          >
            <SafeIcon icon={FiShare2} className="h-3.5 w-3.5" />
            Share
          </button>
          <button
            type="button"
            onClick={printPass}
            className="rsvp-pass-action inline-flex items-center gap-2 rounded-lg bg-brand-light px-3 py-2 text-[9px] font-black uppercase tracking-widest text-accent-navy transition hover:bg-accent-gold"
          >
            <SafeIcon icon={FiPrinter} className="h-3.5 w-3.5" />
            Print pass
          </button>
        </div>
      </div>
      {feedback && (
        <p role="status" className="rsvp-pass-feedback border-t border-brand-light px-5 py-2 text-xs text-text-muted">
          {feedback}
        </p>
      )}
    </article>
  );
}